/**
 * Hook for managing eFolder filter and sort state
 * Provides filtered and sorted document data for the tree view
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import type {
  EncompassDocumentWithAttachments,
  EncompassAttachment,
  EncompassDocumentGroup,
  EfolderGroupingMode
} from '@/types/encompass-documents';
import {
  groupDocumentsByEncompassGroups,
  searchLoanAttachmentContent,
  decorateAttachmentMatch,
  type AttachmentContentMatch
} from '@/services/encompassDocumentService';
import { useDebounce } from '@/hooks/useDebounce';

// Filter types - matches download_status values from encompass_attachments table
export type EfolderStatusFilter = 'all' | 'downloaded' | 'pending' | 'failed' | 'skipped' | 'unavailable' | 'pending_worker' | 'deleted';
export type EfolderFileTypeFilter = 'all' | 'pdf' | 'image' | 'other';
export type EfolderDateRangeFilter = 'all' | '7days' | '30days' | '90days';

// Sort types
export type EfolderSortField = 'name' | 'date' | 'size' | 'status';
export type EfolderSortDirection = 'asc' | 'desc';

export interface EfolderFilters {
  status: EfolderStatusFilter;
  fileType: EfolderFileTypeFilter;
  dateRange: EfolderDateRangeFilter;
  searchQuery: string;
  showInactive: boolean; // Show old/superseded document versions
  groupingMode: EfolderGroupingMode; // How to organize documents
}

export interface EfolderSort {
  field: EfolderSortField;
  direction: EfolderSortDirection;
}

export interface UseEfolderFiltersResult {
  filters: EfolderFilters;
  sort: EfolderSort;
  setFilters: React.Dispatch<React.SetStateAction<EfolderFilters>>;
  setSort: React.Dispatch<React.SetStateAction<EfolderSort>>;
  updateFilter: <K extends keyof EfolderFilters>(key: K, value: EfolderFilters[K]) => void;
  toggleSortDirection: () => void;
  setSortField: (field: EfolderSortField) => void;
  resetFilters: () => void;
  filteredDocuments: EncompassDocumentWithAttachments[];
  groupedDocuments: EncompassDocumentGroup[]; // Documents grouped by Encompass document_groups
  totalAttachments: number;
  filteredAttachments: number;
  inactiveCount: number; // Count of hidden inactive/old version documents
  contentSearchLoading: boolean; // True while the debounced content-search RPC is in flight
  contentSearchError: string | null; // Set when the content-search RPC failed (e.g. network/RLS); UI shows "Content search unavailable"
}

const DEFAULT_FILTERS: EfolderFilters = {
  status: 'all',
  fileType: 'all',
  dateRange: 'all',
  searchQuery: '',
  showInactive: false, // Hide old versions by default
  groupingMode: 'groups' // Default to grouped view
};

const DEFAULT_SORT: EfolderSort = {
  field: 'name',
  direction: 'asc'
};

/**
 * Check if attachment matches status filter
 */
function matchesStatusFilter(attachment: EncompassAttachment, status: EfolderStatusFilter): boolean {
  if (status === 'all') {
    return true;
  }
  return attachment.download_status === status;
}

/**
 * Check if attachment matches file type filter
 */
function matchesFileTypeFilter(attachment: EncompassAttachment, fileType: EfolderFileTypeFilter): boolean {
  if (fileType === 'all') {
    return true;
  }

  const title = attachment.title?.toLowerCase() || '';
  const attachmentFileType = attachment.file_type?.toLowerCase() || '';

  switch (fileType) {
    case 'pdf':
      return title.endsWith('.pdf') || attachmentFileType.includes('pdf');
    case 'image':
      return /\.(jpg|jpeg|png|gif|tiff|tif|webp|bmp)$/i.test(title) ||
             attachmentFileType.includes('image');
    case 'other':
      return !title.endsWith('.pdf') &&
             !/\.(jpg|jpeg|png|gif|tiff|tif|webp|bmp)$/i.test(title) &&
             !attachmentFileType.includes('pdf') &&
             !attachmentFileType.includes('image');
    default:
      return true;
  }
}

/**
 * Check if attachment matches date range filter
 */
function matchesDateRangeFilter(attachment: EncompassAttachment, dateRange: EfolderDateRangeFilter): boolean {
  if (dateRange === 'all') {
    return true;
  }

  const uploadedDate = attachment.uploaded_date ? new Date(attachment.uploaded_date) : null;
  if (!uploadedDate) {
    return true; // Include if no date
  }

  const now = new Date();
  const diffMs = now.getTime() - uploadedDate.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  switch (dateRange) {
    case '7days':
      return diffDays <= 7;
    case '30days':
      return diffDays <= 30;
    case '90days':
      return diffDays <= 90;
    default:
      return true;
  }
}

/**
 * Check if attachment matches search query
 */
function matchesSearchQuery(attachment: EncompassAttachment, searchQuery: string): boolean {
  if (!searchQuery.trim()) {
    return true;
  }

  const query = searchQuery.toLowerCase();
  const title = attachment.title?.toLowerCase() || '';
  const folder = attachment.assigned_folder?.toLowerCase() || '';

  return title.includes(query) || folder.includes(query);
}

/**
 * Check if attachment matches inactive/version filter
 * When showInactive is false, only show active (current version) documents
 */
function matchesInactiveFilter(attachment: EncompassAttachment, showInactive: boolean): boolean {
  if (showInactive) {
    return true; // Show all documents including inactive
  }
  // Hide inactive documents (is_active === false means superseded version)
  return attachment.is_active !== false;
}

/**
 * Sort attachments by field and direction
 */
function sortAttachments(
  a: EncompassAttachment,
  b: EncompassAttachment,
  sort: EfolderSort
): number {
  let comparison = 0;

  switch (sort.field) {
    case 'name':
      comparison = (a.title || '').localeCompare(b.title || '');
      break;
    case 'date': {
      const dateA = a.uploaded_date ? new Date(a.uploaded_date).getTime() : 0;
      const dateB = b.uploaded_date ? new Date(b.uploaded_date).getTime() : 0;
      comparison = dateA - dateB;
      break;
    }
    case 'size':
      comparison = (a.file_size || 0) - (b.file_size || 0);
      break;
    case 'status': {
      const statusOrder: Record<string, number> = {
        downloaded: 1,
        pending: 2,
        pending_worker: 3,
        failed: 4,
        deleted: 5,
        skipped: 6,
        unavailable: 7
      };
      comparison = (statusOrder[a.download_status] || 8) - (statusOrder[b.download_status] || 8);
      break;
    }
  }

  return sort.direction === 'asc' ? comparison : -comparison;
}

/**
 * Custom hook for managing eFolder filters and sorting.
 *
 * Content search: when `searchQuery` has >= 2 chars (after trim), a debounced
 * RPC fires against `search_loan_attachment_content` for `loanId` and decorates
 * matching attachments with their snippet + page number. The synchronous
 * title/folder filter runs every keystroke and is never delayed by the RPC.
 */
export function useEfolderFilters(
  documents: EncompassDocumentWithAttachments[],
  loanId: string | null | undefined
): UseEfolderFiltersResult {
  const [filters, setFilters] = useState<EfolderFilters>(DEFAULT_FILTERS);
  const [sort, setSort] = useState<EfolderSort>(DEFAULT_SORT);
  const [contentMatches, setContentMatches] = useState<Map<string, AttachmentContentMatch>>(
    () => new Map()
  );
  const [contentSearchLoading, setContentSearchLoading] = useState(false);
  const [contentSearchError, setContentSearchError] = useState<string | null>(null);

  // Monotonic request id so superseded RPCs can't decorate state. Aborting
  // alone isn't sufficient under React StrictMode's double-effect invocations.
  const requestIdRef = useRef(0);
  const debouncedQuery = useDebounce(filters.searchQuery, 300);

  useEffect(() => {
    const trimmed = debouncedQuery.trim();

    // Always clear matches when the query falls below the threshold or the
    // loan changes — stale decorations from a previous loan would be wrong.
    //
    // Bump requestIdRef first: a previously-issued RPC may resolve AFTER
    // this early return (network packet already in flight, abort lost the
    // race). Without this bump it could repopulate state because its
    // captured `myRequestId` would still match `requestIdRef.current`.
    if (!loanId || trimmed.length < 2) {
      requestIdRef.current += 1;
      setContentMatches(prev => (prev.size === 0 ? prev : new Map()));
      setContentSearchLoading(false);
      setContentSearchError(null);
      return;
    }

    const myRequestId = ++requestIdRef.current;
    const controller = new AbortController();

    setContentSearchLoading(true);
    setContentSearchError(null);

    searchLoanAttachmentContent(loanId, trimmed, controller.signal)
      .then(results => {
        if (myRequestId !== requestIdRef.current) {
          return; // a newer query has been issued; drop this result
        }
        const next = new Map<string, AttachmentContentMatch>();
        for (const r of results) {
          next.set(r.attachment_id, r);
        }
        setContentMatches(next);
        setContentSearchLoading(false);
      })
      .catch(err => {
        if (myRequestId !== requestIdRef.current || controller.signal.aborted) {
          return;
        }
        setContentSearchError(err instanceof Error ? err.message : 'Content search failed');
        setContentSearchLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [debouncedQuery, loanId]);

  // Update a single filter
  const updateFilter = useCallback(<K extends keyof EfolderFilters>(
    key: K,
    value: EfolderFilters[K]
  ) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Toggle sort direction
  const toggleSortDirection = useCallback(() => {
    setSort(prev => ({
      ...prev,
      direction: prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Set sort field (resets to ascending)
  const setSortField = useCallback((field: EfolderSortField) => {
    setSort(prev => ({
      field,
      direction: prev.field === field ? (prev.direction === 'asc' ? 'desc' : 'asc') : 'asc'
    }));
  }, []);

  // Reset all filters
  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setSort(DEFAULT_SORT);
  }, []);

  // Calculate total attachments count (respects showInactive filter for accurate denominator)
  const totalAttachments = useMemo(() => {
    return documents.reduce((sum, doc) =>
      sum + doc.attachments.filter(att => matchesInactiveFilter(att, filters.showInactive)).length, 0
    );
  }, [documents, filters.showInactive]);

  // Filter, decorate, and sort documents.
  //
  // Search composition: an attachment passes the search step when *either*
  // the client-side title/folder match returns true OR the server-side
  // content-search RPC returned a hit for its id. Other filters (status,
  // file type, date, inactive) still gate the result so content matches
  // can't bypass them.
  const filteredDocuments = useMemo(() => {
    const trimmedQuery = filters.searchQuery.trim();
    const hasQuery = trimmedQuery.length > 0;

    return documents
      .map(doc => ({
        ...doc,
        attachments: doc.attachments
          .filter(att => {
            if (!matchesStatusFilter(att, filters.status)) {
return false;
}
            if (!matchesFileTypeFilter(att, filters.fileType)) {
return false;
}
            if (!matchesDateRangeFilter(att, filters.dateRange)) {
return false;
}
            if (!matchesInactiveFilter(att, filters.showInactive)) {
return false;
}
            if (!hasQuery) {
return true;
}
            return matchesSearchQuery(att, filters.searchQuery) || contentMatches.has(att.id);
          })
          .map(att => hasQuery
            ? decorateAttachmentMatch(att, filters.searchQuery, contentMatches.get(att.id))
            : att)
          .sort((a, b) => sortAttachments(a, b, sort))
      }))
      .filter(doc => doc.attachments.length > 0);
  }, [documents, filters, sort, contentMatches]);

  // Calculate filtered attachments count
  const filteredAttachments = useMemo(() => {
    return filteredDocuments.reduce((sum, doc) => sum + doc.attachments.length, 0);
  }, [filteredDocuments]);

  // Calculate count of inactive (old version) documents
  const inactiveCount = useMemo(() => {
    return documents.reduce((sum, doc) =>
      sum + doc.attachments.filter(att => att.is_active === false).length, 0
    );
  }, [documents]);

  // Group filtered documents by their Encompass document_groups
  const groupedDocuments = useMemo(() => {
    if (filters.groupingMode === 'flat') {
      return []; // Not used in flat mode
    }
    return groupDocumentsByEncompassGroups(filteredDocuments);
  }, [filteredDocuments, filters.groupingMode]);

  return {
    filters,
    sort,
    setFilters,
    setSort,
    updateFilter,
    toggleSortDirection,
    setSortField,
    resetFilters,
    filteredDocuments,
    groupedDocuments,
    totalAttachments,
    filteredAttachments,
    inactiveCount,
    contentSearchLoading,
    contentSearchError
  };
}
